// src/pages/meetings/EventCalendar.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  // ListItemText,
  Chip,
  Divider,
  // CircularProgress,
  Alert,
  Tooltip,
  useTheme,
  alpha,
  Skeleton,
} from "@mui/material";
import { StaticDatePicker, PickersDay } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, isSameDay } from "date-fns";
import { useLazyGetMeetingsQuery } from "../../store/api";
import { toast } from "react-toastify";

/* --------------------------------------------------------------
   Simplified date parsing - your API dates are already in UTC
   -------------------------------------------------------------- */
const parseApiDate = (dateStr) => {
  return new Date(dateStr.replace(" ", "T") + "Z");
};

/** Stable empty default — inline `= []` creates a new array every render and can infinite-loop effects */
const EMPTY_MEETINGS = [];

const EventCalendar = ({
  meetings: externalMeetings = EMPTY_MEETINGS,
  loading: externalLoading = false,
}) => {
  const [fetchMeetingsTrigger] = useLazyGetMeetingsQuery();
  const theme = useTheme();
  const [internalMeetings, setInternalMeetings] = useState([]);
  const [internalLoading, setInternalLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [eventsOnDate, setEventsOnDate] = useState([]);
  const [, setHoveredDate] = useState(null);
  const hasExternalMeetings = externalMeetings.length > 0;

  // Use external data if provided, otherwise fetch internally
  const meetings = hasExternalMeetings ? externalMeetings : internalMeetings;
  const loading = hasExternalMeetings ? externalLoading : internalLoading;

  /* ------------------- FETCH MEETINGS ONLY IF NOT PROVIDED ------------------- */
  useEffect(() => {
    if (hasExternalMeetings) return;

    let cancelled = false;
    (async () => {
      try {
        setInternalLoading(true);
        const res = await fetchMeetingsTrigger({
          "relations[0]": "status",
          "relations[1]": "lead",
        }).unwrap();
        if (cancelled) return;
        const data = res?.data?.data ?? res?.data ?? res ?? [];
        setInternalMeetings(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) {
          toast.error(err.message || "Failed to load meetings");
        }
      } finally {
        if (!cancelled) setInternalLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasExternalMeetings, fetchMeetingsTrigger]);

  /* ------------------- OPTIMIZE EVENT FILTERING ------------------- */
  useEffect(() => {
    if (!meetings.length) {
      setEventsOnDate([]);
      return;
    }

    // Use useMemo pattern for expensive operations
    const filteredEvents = meetings
      .filter((m) => {
        try {
          const start = new Date(m.start_date_time.replace(" ", "T") + "Z");
          return isSameDay(start, selectedDate);
        } catch (error) {
          return false;
        }
      })
      .sort((a, b) => a.start_date_time.localeCompare(b.start_date_time));

    setEventsOnDate(filteredEvents);
  }, [selectedDate, meetings]);

  /* ------------------- RENDER DAY WITH EVENT BARS ------------------- */
  const renderDay = (date, _selectedDates, pickersDayProps) => {
    const dayMeetings = meetings
      .filter((m) => {
        const start = parseApiDate(m.start_date_time);
        return isSameDay(start, date);
      })
      .sort((a, b) => a.start_date_time.localeCompare(b.start_date_time));

    const hasMeetings = dayMeetings.length > 0;
    const isToday = isSameDay(date, new Date());
    const isSelected = isSameDay(date, selectedDate);

    const maxVisible = 3;
    const visibleMeetings = dayMeetings.slice(0, maxVisible);

    const bars = visibleMeetings.map((m, idx) => {
      const start = parseApiDate(m.start_date_time);
      const end = parseApiDate(m.end_date_time);

      const startH = start.getHours() + start.getMinutes() / 60;
      const endH = end.getHours() + end.getMinutes() / 60;
      const leftPct = (startH / 24) * 100;
      const widthPct = ((endH - startH) / 24) * 100;

      const color = m.status?.color || theme.palette.primary.main;

      const tooltip = (
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            gutterBottom
            color="primary"
          >
            {m.lead?.name || "Unknown Client"}
          </Typography>
          <Chip
            label={m.status?.name || "Meeting"}
            size="small"
            sx={{
              bgcolor: color,
              color: "#fff",
              fontWeight: 500,
              mb: 1,
            }}
          />
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            gutterBottom
          >
            {format(start, "h:mm a")} – {format(end, "h:mm a")} (UTC)
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, mb: 1.5 }}>
            {m.description || "No description provided"}
          </Typography>
          {m.lead?.email && (
            <Typography variant="caption" display="block" color="info.main">
              📧 {m.lead.email}
            </Typography>
          )}
          {m.lead?.phone && (
            <Typography variant="caption" display="block" color="info.main">
              📞 {m.lead.phone}
            </Typography>
          )}
        </Box>
      );

      return (
        <Tooltip key={m.id} title={tooltip} arrow placement="top">
          <Box
            sx={{
              position: "absolute",
              top: 24 + idx * 18,
              left: `${Math.max(2, Math.min(96, leftPct))}%`,
              width: `${Math.min(94, widthPct)}%`,
              height: 14,
              bgcolor: color,
              borderRadius: "4px",
              px: 0.5,
              overflow: "hidden",
              whiteSpace: "nowrap",
              fontSize: "0.6rem",
              fontWeight: 600,
              color: "#fff",
              cursor: "pointer",
              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
              display: "flex",
              alignItems: "center",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              },
            }}
          >
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {format(start, "h:mm")} {m.lead?.name?.split(" ")[0] || "Client"}
            </Box>
          </Box>
        </Tooltip>
      );
    });

    return (
      <Box
        sx={{
          position: "relative",
          height: 70,
          overflow: "hidden",
          cursor: "pointer",
        }}
        onMouseEnter={() => setHoveredDate(date)}
        onMouseLeave={() => setHoveredDate(null)}
      >
        <PickersDay
          {...pickersDayProps}
          onClick={(e) => {
            pickersDayProps.onClick?.(e);
            setSelectedDate(date);
          }}
          sx={{
            position: "relative",
            width: 36,
            height: 36,
            fontSize: "0.875rem",
            fontWeight: isToday ? 700 : hasMeetings ? 600 : 400,

            // Today's date styling
            ...(isToday &&
              !isSelected && {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                border: `1px solid ${theme.palette.primary.main}`,
                color: theme.palette.primary.main,
              }),

            // Dates with meetings styling
            ...(hasMeetings &&
              !isSelected &&
              !isToday && {
                backgroundColor: "#FFF8E1",
                border: `1px solid #FFD54F`,
                color: "#5D4037",
                "&:hover": {
                  backgroundColor: "#FFECB3",
                  transform: "scale(1.05)",
                },
              }),

            // Selected date styling
            ...(isSelected && {
              backgroundColor: hasMeetings
                ? "#FFC107"
                : theme.palette.primary.main,
              color: hasMeetings ? "#5D4037" : "#fff",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: hasMeetings
                  ? "#FFB300"
                  : theme.palette.primary.dark,
              },
              transform: "scale(1.1)",
            }),

            // Hover effect for all dates
            "&:hover": {
              backgroundColor: hasMeetings
                ? "#FFECB3"
                : alpha(theme.palette.primary.main, 0.1),
              transform: "scale(1.05)",
              transition: "all 0.2s ease",
            },

            // Animation
            transition: "all 0.2s ease",
          }}
        />

        {/* Event Bars */}
        {bars}

        {/* Meeting count badge */}
        {hasMeetings && (
          <Box
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              width: 16,
              height: 16,
              borderRadius: "50%",
              backgroundColor: isSelected ? "#5D4037" : "#FF6F00",
              color: "#fff",
              fontSize: "0.5rem",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
            }}
          >
            {dayMeetings.length}
          </Box>
        )}

        {/* +X More for overflow */}
        {dayMeetings.length > maxVisible && (
          <Box
            sx={{
              position: "absolute",
              bottom: 2,
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: "0.55rem",
              color: isSelected
                ? hasMeetings
                  ? "#5D4037"
                  : "#fff"
                : "text.secondary",
              fontWeight: 600,
              backgroundColor: isSelected ? alpha("#fff", 0.2) : "transparent",
              px: 0.5,
              borderRadius: 1,
            }}
          >
            +{dayMeetings.length - maxVisible}
          </Box>
        )}
      </Box>
    );
  };

  /* ------------------- SKELETON LOADERS ------------------- */
  const CalendarSkeleton = () => (
    <Box sx={{ p: 2 }}>
      <Skeleton
        variant="rectangular"
        width="100%"
        height={400}
        sx={{ borderRadius: 2, mb: 2 }}
      />
      <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Skeleton variant="circular" width={40} height={40} />
        <Skeleton variant="circular" width={40} height={40} />
      </Box>
    </Box>
  );

  const MeetingsListSkeleton = () => (
    <Box sx={{ p: 3 }}>
      <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
      <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
      <Skeleton
        variant="rectangular"
        width="100%"
        height={100}
        sx={{ borderRadius: 2, mb: 2 }}
      />
      <Skeleton
        variant="rectangular"
        width="100%"
        height={100}
        sx={{ borderRadius: 2, mb: 2 }}
      />
      <Skeleton
        variant="rectangular"
        width="100%"
        height={100}
        sx={{ borderRadius: 2 }}
      />
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: "auto" }}>
        {/* Header */}
        <Box sx={{ textAlign: "start", mb: 4 }}>
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: "primary.main",
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Event Calendar
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Manage your meetings and appointments
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          {/* ---------- CALENDAR CARD ---------- */}
          <Paper
            elevation={8}
            sx={{
              p: 3,
              flex: 1,
              maxWidth: { xs: "100%", lg: 800 },
              borderRadius: 4,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
              backdropFilter: "blur(10px)",
            }}
          >
            {loading ? (
              <CalendarSkeleton />
            ) : (
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                value={selectedDate}
                onChange={setSelectedDate}
                renderDay={renderDay}
                disablePast
                slotProps={{
                  actionBar: {
                    actions: ["today"],
                    sx: { px: 2, pb: 2 },
                  },
                  layout: {
                    sx: {
                      "& .MuiPickersCalendarHeader-root": {
                        px: 2,
                        pt: 2,
                      },
                    },
                  },
                }}
                sx={{
                  "& .MuiPickersCalendarHeader-root": {
                    mb: 1,
                  },
                  "& .MuiPickersCalendarHeader-label": {
                    fontSize: "1.1rem",
                    fontWeight: 600,
                  },
                  "& .MuiDayCalendar-weekDayLabel": {
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: "text.secondary",
                  },
                  "& .MuiPickersArrowSwitcher-button": {
                    color: "primary.main",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    },
                  },
                }}
              />
            )}
          </Paper>

          {/* ---------- MEETINGS DETAILS CARD ---------- */}
          <Paper
            elevation={8}
            sx={{
              p: 3,
              flex: 1,
              minHeight: 400,
              borderRadius: 4,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
              backdropFilter: "blur(10px)",
            }}
          >
            {loading ? (
              <MeetingsListSkeleton />
            ) : (
              <>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ fontWeight: 600, color: "primary.main" }}
                >
                  📅 Meetings on {format(selectedDate, "MMMM d, yyyy")}
                </Typography>
                <Divider sx={{ mb: 3, mt: 1 }} />

                {eventsOnDate.length === 0 ? (
                  <Alert
                    severity="info"
                    sx={{
                      mt: 2,
                      borderRadius: 3,
                      "& .MuiAlert-message": {
                        width: "100%",
                        textAlign: "center",
                      },
                    }}
                  >
                    <Typography variant="body1" fontWeight={500}>
                      No meetings scheduled for this date
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Enjoy your free time! 🎉
                    </Typography>
                  </Alert>
                ) : (
                  <List
                    disablePadding
                    sx={{ maxHeight: 500, overflow: "auto", pr: 1 }}
                  >
                    {eventsOnDate.map((event, idx) => {
                      const start = parseApiDate(event.start_date_time);
                      const end = parseApiDate(event.end_date_time);
                      const color =
                        event.status?.color || theme.palette.primary.main;

                      return (
                        <React.Fragment key={event.id}>
                          {idx > 0 && <Divider component="li" sx={{ my: 2 }} />}
                          <ListItem
                            alignItems="flex-start"
                            sx={{
                              py: 2.5,
                              px: 3,
                              borderRadius: 3,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: alpha(color, 0.05),
                                transform: "translateX(4px)",
                              },
                            }}
                          >
                            <Box sx={{ width: "100%" }}>
                              {/* Header with time and status */}
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  mb: 1.5,
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    sx={{ mb: 0.5 }}
                                  >
                                    {event.lead?.name || "Unknown Client"}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    ⏰ {format(start, "h:mm a")} -{" "}
                                    {format(end, "h:mm a")}
                                  </Typography>
                                </Box>
                                <Chip
                                  label={event.status?.name || "Meeting"}
                                  size="small"
                                  sx={{
                                    bgcolor: color,
                                    color: "#fff",
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                  }}
                                />
                              </Box>

                              {/* Description */}
                              {event.description && (
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 2, lineHeight: 1.5 }}
                                >
                                  {event.description}
                                </Typography>
                              )}

                              {/* Contact Info */}
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 2,
                                  flexWrap: "wrap",
                                }}
                              >
                                {event.lead?.email && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    📧 {event.lead.email}
                                  </Typography>
                                )}
                                {event.lead?.phone && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    📞 {event.lead.phone}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                  </List>
                )}
              </>
            )}
          </Paper>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default EventCalendar;
